import {
    ESCAPE_KEYBOARD_LISTENER_ID,
    FRAME_INIT_MESSAGE_TYPE,
    KeyDownListenerId,
    MAIN_LISTENER_ID,
    Message,
    MessageListenerId,
    MessageType,
    METADATA_CHANGE_MESSAGE_TYPE,
    K_KEYBOARD_LISTENER_ID,
    PLAYER_CLOSE_MESSAGE_TYPE,
    PLAYER_INTERSECTION_CHANGE_MESSAGE_TYPE,
    PLAYER_OPEN_MESSAGE_TYPE,
    PlayerIntersectionChangeMessagePayload,
    VIDEO_RENDER_LISTENER_ID,
    SPACE_KEYBOARD_LISTENER_ID,
    PAGE_SCROLL_REQUEST_MESSAGE_TYPE,
    PageScrollMessagePayload,
    PLAYER_CLOSE_REQUEST_MESSAGE_TYPE,
    ACTION_BUTTON_CLICK_MESSAGE_TYPE,
} from './type/communication.type';
import {
    FlowConfigPlayerVideo,
    FlowConfigPlayerVideoButton,
    FlowConfigType,
    HIDE_PLAYER_ACTION_TYPE,
    OPEN_VIDEO_ACTION_TYPE,
    REAL_FOOTAGE_AND_REAL_AUDIO_CONTENT_VARIANT,
    GENERATED_FOOTAGE_AND_REAL_AUDIO_CONTENT_VARIANT,
    GENERATED_FOOTAGE_AND__GENERATED_AUDIO_CONTENT_VARIANT,
    REAL_FOOTAGE_AND_GENERATED_AUDIO_CONTENT_VARIANT,
    UNKNOWN_CONTENT_VARIANT,
} from './type/flow.type';
import {
    PageMetadata,
    PlayerState,
    VideoWatchTimeState,
} from './type/store.type';

import { getIcon } from './util/getIcon.util';
import { getIsReducedMotion } from './util/getIsReducedMotion';
import { logDebug } from './util/logDebug.util';
import { matchTemplateStr } from './util/matchTemplateStr.util';
import { getEventTracker, initializeEventTracker } from './util/analytics.util';
import { getIsContentWrapped } from './util/getIsContentWrapped';
import { normalizeUrl } from './util/normalizeUrl.util';
import { hash } from './util/hash.util';

// NOTE: Selectors
const HISTORY_CONTAINER_SELECTOR = 'header .history-container';
const TITLE_SELECTOR = 'header h2';
const BADGE_SELECTOR = 'header .title-container__badge';
const BADGE_INITIALS_SELECTOR = 'header .title-container__badge-initials';
const BADGE_INITIALS_TEXT_SELECTOR =
    'header .title-container__badge-initials-text';
const BADGE_INITIALS_ICON_SELECTOR =
    'header .title-container__badge-initials-icon';
const BADGE_ICON_VERIFIED_SELECTOR =
    'header .title-container__badge-initials-icon .icon-verified';
const BADGE_ICON_INFO_SELECTOR =
    'header .title-container__badge-initials-icon .icon-info';
const BADGE_ICON_QUESTION_SELECTOR =
    'header .title-container__badge-initials-icon .icon-question';
const BADGE_EXTRA_SELECTOR = 'header .title-container__badge-extra';
const BADGE_EXTRA_CONTENT_SELECTOR =
    'header .title-container__badge-extra-content';
const BADGE_EXTRA_NAME_SELECTOR = 'header .title-container__badge-extra-name';
const BADGE_EXTRA_ROLE_SELECTOR = 'header .title-container__badge-extra-role';
const BADGE_EXTRA_CONTENT_VARIANT_SELECTOR =
    'header .title-container__badge-extra-content-variant';
const VIDEO_SELECTOR = '.video-element';
const TRACK_SELECTOR = '#player-video track';
const BUTTON_CONTAINER_SELECTOR = '.button-container';
const CAPTION_TEXT_CONTAINER_SELECTOR = '.caption-text__container';
const CAPTION_TEXT_SELECTOR = '.caption-text';
const CONTROL_CLOSE_SELECTOR = '.control-button__close';
const CONTROL_MUTE_SELECTOR = '.control-button__mute';
const CONTROL_GO_BACK_SELECTOR = '.control-button__goback';
const CONTROLS_CONTAINER_SELECTOR = '.controls-container';
const LOADER_CONTAINER_SELECTOR = '.loader-container';
const REPLAY_CONTAINER_SELECTOR = '.replay-container';
const NAME_PLATE_CONTAINER_SELECTOR = '.nameplate-container';
const NAME_PLATE_CONTAINER_BLOCK_SELECTOR = '.nameplate-container__block';
const NAME_PLATE_AUTHOR_SELECTOR = '.nameplate-container__author';
const NAME_PLATE_ROLE_SELECTOR = '.nameplate-container__role';
const NAME_PLATE_AUTHOR_TEXT_SELECTOR = '.name-plate-container__author-text';
const NAME_PLATE_ROLE_TEXT_SELECTOR = '.name-plate-container__role-text';
const PREVIEW_LABEL_SELECTOR = '.preview-label';
const LOGO_SELECTOR = '.logo';
const FOOTER_SELECTOR = 'footer';
const BRAND_SPACE_SELECTOR = '.brand-space';
const SPEED_INDICATOR_SELECTOR = '.speed-indicator';
const DOMAIN_MISMATCH_CONTAINER_SELECTOR = '.domain-mismatch-container';

// NOTE: Storage keys
const PLAYER_STATE_LOCAL_STORAGE_KEY = 'reelflow_player_state';
const VIEWED_NAMES_SESSION_STORAGE_KEY = 'reelflow_viewed_names';

// NOTE: Workspace Data
const workspace = window.__reelflowData.workspace;
const flowConfig = window.__reelflowData.flowConfig;
const flowConfigPlayer = flowConfig.player;

let pageMetadata: PageMetadata;
let playerStateMap: Record<string, PlayerState> = {};
const isMuteStateChangedMap: Record<string, boolean> = {};
const videoWatchTimeState: Record<string, VideoWatchTimeState> = {};

const messageHistory: { message: Message; receivedAt: number }[] = [];
const messageEventListenerMap: {
    [key in MessageListenerId]?: (message: Message<any>) => void;
} = {};

const keyDownListenerMap: {
    [key in KeyDownListenerId]?: (event: KeyboardEvent) => void;
} = {};

function initializePlayerState() {
    var flowId = flowConfig.code;
    var type = flowConfig.type;
    const workspaceId = workspace.code;

    var cachedPlayerStateMap = null;

    try {
        var rawCachedPlayerStateMap = sessionStorage.getItem(
            PLAYER_STATE_LOCAL_STORAGE_KEY,
        );

        if (rawCachedPlayerStateMap) {
            cachedPlayerStateMap = JSON.parse(rawCachedPlayerStateMap)[
                workspaceId
            ];
        }
    } catch (ex) {}

    playerStateMap[flowId] = {
        currentVideoId: flowConfigPlayer.videos[0].id,
        videoIdHistory: [flowConfigPlayer.videos[0].id],
        controlMap: { isMuted: true, isPlaying: false },
        extra: {
            currentTime: 0,
            duration: 0,
            uiElementsHidden: false,
            isButtonListAnimated: false,
        },
    };

    if (cachedPlayerStateMap && type in cachedPlayerStateMap) {
        playerStateMap[flowId].controlMap.isMuted = cachedPlayerStateMap[type];

        isMuteStateChangedMap[flowId] = true;
    }
}

function initializeVideoWatchTimeState() {
    const flowId = flowConfig.code;

    videoWatchTimeState[flowId] = {
        lastPlaybackTime: null,
        lastAnalyticsEventTrackedAt: 0,
        isCurrentlyTracking: false,
        dataMap: {},
    };
}

function cachePlayerMuteState() {
    const flowId = flowConfig.code;
    const type = flowConfig.type;
    const workspaceId = workspace.code;

    try {
        const currentPlayerStateMap = JSON.parse(
            sessionStorage.getItem(PLAYER_STATE_LOCAL_STORAGE_KEY) || '{}',
        );

        currentPlayerStateMap[workspaceId] = {
            ...(currentPlayerStateMap[workspaceId] || {}),
            [type]: playerStateMap[flowId].controlMap.isMuted,
        };

        sessionStorage.setItem(
            PLAYER_STATE_LOCAL_STORAGE_KEY,
            JSON.stringify(currentPlayerStateMap),
        );
    } catch (ex) {
        getEventTracker().track('error', {
            name: 'player_state_cache',
            // @ts-expect-error
            message: (ex && ex.message) || '',
        });
    }
}

function setIsNameViewed(name: string, role: string): void {
    try {
        let viewedNames: string[] = [];

        const rawViewedName = sessionStorage.getItem(
            VIEWED_NAMES_SESSION_STORAGE_KEY,
        );

        if (rawViewedName) {
            viewedNames = JSON.parse(rawViewedName);
        }

        const nameHash = hash((name || '') + (role || ''));

        if (!viewedNames.includes(nameHash)) {
            viewedNames.push(nameHash);
        }

        sessionStorage.setItem(
            VIEWED_NAMES_SESSION_STORAGE_KEY,
            JSON.stringify(viewedNames),
        );
    } catch (ex) {
        logDebug('[ReelFlow][setIsNameViewed] Error', ex);
    }
}

function getIsNameViewed(name: string, role: string): boolean {
    // const isDev = true;

    // // NOTE: For development purposes, we don't want to check if the name has been viewed
    // if (isDev) {
    //     return false;
    // }

    try {
        let viewedNames: string[] = [];

        const rawViewedName = sessionStorage.getItem(
            VIEWED_NAMES_SESSION_STORAGE_KEY,
        );

        if (rawViewedName) {
            viewedNames = JSON.parse(rawViewedName);
        }

        const nameHash = hash((name || '') + (role || ''));

        return viewedNames.includes(nameHash);
    } catch (ex) {
        return false;
    }
}

function startWatchTimeTracking(videoId: string, currentTime: number) {
    const flowId = flowConfig.code;

    // Initialize watch time for this video if it doesn't exist
    if (!videoWatchTimeState[flowId].dataMap[videoId]) {
        videoWatchTimeState[flowId].dataMap[videoId] = {
            watchTimeMs: 0,
        };
    }

    videoWatchTimeState[flowId].lastPlaybackTime = currentTime;
    videoWatchTimeState[flowId].isCurrentlyTracking = true;

    logDebug('[ReelFlow][startWatchTimeTracking] Started tracking', {
        videoWatchTimeState,
        currentTime,
    });
}

function stopWatchTimeTracking(videoId: string, currentTime: number) {
    const flowId = flowConfig.code;

    if (
        !videoWatchTimeState[flowId].isCurrentlyTracking ||
        videoWatchTimeState[flowId].lastPlaybackTime === null
    ) {
        return;
    }

    const elapsedTime =
        (currentTime - videoWatchTimeState[flowId].lastPlaybackTime) * 1000; // Convert to milliseconds

    // Only add positive time (in case of seeking backwards)
    if (elapsedTime > 0 && elapsedTime < 10000) {
        // Sanity check: ignore if > 10 seconds (likely a seek)
        if (videoWatchTimeState[flowId].dataMap[videoId]) {
            videoWatchTimeState[flowId].dataMap[videoId].watchTimeMs +=
                elapsedTime;
        }
    }

    videoWatchTimeState[flowId].lastPlaybackTime = null;
    videoWatchTimeState[flowId].isCurrentlyTracking = false;

    logDebug('[ReelFlow][stopWatchTimeTracking] Stopped tracking', {
        videoWatchTimeState,
        currentTime,
    });
}

function updateWatchTime(videoId: string, currentTime: number) {
    const flowId = flowConfig.code;

    if (
        !videoWatchTimeState[flowId].isCurrentlyTracking ||
        videoWatchTimeState[flowId].lastPlaybackTime === null
    ) {
        return;
    }

    const timeDelta =
        currentTime - videoWatchTimeState[flowId].lastPlaybackTime;

    // NOTE: Only count forward playback with reasonable delta (0.01 to 2 seconds)
    // NOTE: This handles normal playback and filters out seeks
    if (timeDelta > 0.01 && timeDelta < 2) {
        const elapsedTimeMs = timeDelta * 1000;

        if (videoWatchTimeState[flowId].dataMap[videoId]) {
            videoWatchTimeState[flowId].dataMap[videoId].watchTimeMs +=
                elapsedTimeMs;
        }
    }

    videoWatchTimeState[flowId].lastPlaybackTime = currentTime;
}

function getVideoWatchTimeMs(videoId: string) {
    const flowId = flowConfig.code;

    const value = Math.floor(
        videoWatchTimeState[flowId].dataMap?.[videoId]?.watchTimeMs || 0,
    );

    return value;
}

function getFlowTotalWatchTimeMs() {
    const flowId = flowConfig.code;

    if (!videoWatchTimeState[flowId].dataMap) {
        return 0;
    }

    const value = Math.floor(
        Object.values(videoWatchTimeState[flowId].dataMap).reduce(
            (acc, curr) => acc + curr.watchTimeMs,
            0,
        ),
    );

    return value;
}

function getContentVariantText(contentVariant?: number): string {
    switch (contentVariant) {
        case REAL_FOOTAGE_AND_REAL_AUDIO_CONTENT_VARIANT:
            return 'Real video footage and audio';
        case GENERATED_FOOTAGE_AND_REAL_AUDIO_CONTENT_VARIANT:
            return 'AI-generated video, real audio';
        case GENERATED_FOOTAGE_AND__GENERATED_AUDIO_CONTENT_VARIANT:
            return 'Video and audio is AI-generated';
        case REAL_FOOTAGE_AND_GENERATED_AUDIO_CONTENT_VARIANT:
            return 'Real video, AI-generated audio';
        default:
            return 'Video may contain AI-generated content';
    }
}

function setBadgeIcon(
    contentVariant: number | undefined,
    iconContainerNode: HTMLElement | null,
    verifiedNode: HTMLElement | null,
    infoNode: HTMLElement | null,
    questionNode: HTMLElement | null,
    hasAuthorInfo: boolean,
) {
    // Hide all icons first
    verifiedNode?.classList.add('hidden');
    infoNode?.classList.add('hidden');
    questionNode?.classList.add('hidden');

    // Remove animation class from container
    iconContainerNode?.classList.remove('animate-to-content');

    // Show appropriate icon based on content variant
    switch (contentVariant) {
        case undefined:
        case UNKNOWN_CONTENT_VARIANT: // 0
            // Show question icon for unknown content
            questionNode?.classList.remove('hidden');
            // If has author info, the icon should animate to align with content text
            if (hasAuthorInfo) {
                iconContainerNode?.classList.add('animate-to-content');
            }
            break;
        case REAL_FOOTAGE_AND_REAL_AUDIO_CONTENT_VARIANT: // 1
            // Show verified (checkmark) icon for real content
            verifiedNode?.classList.remove('hidden');
            // If has author info, the icon should animate to align with content text
            if (hasAuthorInfo) {
                iconContainerNode?.classList.add('animate-to-content');
            }
            break;
        case GENERATED_FOOTAGE_AND_REAL_AUDIO_CONTENT_VARIANT: // 2
        case GENERATED_FOOTAGE_AND__GENERATED_AUDIO_CONTENT_VARIANT: // 3
        case REAL_FOOTAGE_AND_GENERATED_AUDIO_CONTENT_VARIANT: // 4
            // For variants 2,3,4: show info icon for any generated content
            infoNode?.classList.remove('hidden');
            // If has author info, the icon should animate to align with content text
            if (hasAuthorInfo) {
                iconContainerNode?.classList.add('animate-to-content');
            }
            break;
        default:
            // No icon for invalid content variant values
            break;
    }
}

function renderVideo() {
    // TODO: Use "flowConfig" and get rid of "flow
    var flowState = playerStateMap[flowConfig.code];

    const eventTracker = getEventTracker();

    let isVideoStartedTracked = false;
    let isVideoMouseDownEffectRunning = false;
    let autoPlayerTimers: NodeJS.Timeout[] = [];
    let videoMouseDownTimerId: NodeJS.Timeout | null = null;

    var historyContainerNode = document.querySelector<HTMLElement>(
        HISTORY_CONTAINER_SELECTOR,
    );
    var titleNode = document.querySelector<HTMLElement>(TITLE_SELECTOR);
    var badgeNode = document.querySelector<HTMLElement>(BADGE_SELECTOR);
    var badgeInitialsNode = document.querySelector<HTMLElement>(
        BADGE_INITIALS_SELECTOR,
    );
    var badgeInitialsTextNode = document.querySelector<HTMLElement>(
        BADGE_INITIALS_TEXT_SELECTOR,
    );
    var badgeInitialsIconNode = document.querySelector<HTMLElement>(
        BADGE_INITIALS_ICON_SELECTOR,
    );
    const badgeIconVerifiedNode = document.querySelector<HTMLElement>(
        BADGE_ICON_VERIFIED_SELECTOR,
    );
    const badgeIconInfoNode = document.querySelector<HTMLElement>(
        BADGE_ICON_INFO_SELECTOR,
    );
    const badgeIconQuestionNode = document.querySelector<HTMLElement>(
        BADGE_ICON_QUESTION_SELECTOR,
    );
    var badgeExtraNode =
        document.querySelector<HTMLElement>(BADGE_EXTRA_SELECTOR);
    var badgeExtraContentNode = document.querySelector<HTMLElement>(
        BADGE_EXTRA_CONTENT_SELECTOR,
    );
    var badgeExtraNameNode = document.querySelector<HTMLElement>(
        BADGE_EXTRA_NAME_SELECTOR,
    );
    var badgeExtraRoleNode = document.querySelector<HTMLElement>(
        BADGE_EXTRA_ROLE_SELECTOR,
    );
    var badgeExtraContentVariantNode = document.querySelector<HTMLElement>(
        BADGE_EXTRA_CONTENT_VARIANT_SELECTOR,
    );
    let videoPlaceholderNode =
        document.querySelector<HTMLVideoElement>(VIDEO_SELECTOR);
    const closeButtonNode = document.querySelector<HTMLButtonElement>(
        CONTROL_CLOSE_SELECTOR,
    );
    const muteButtonNode = document.querySelector<HTMLButtonElement>(
        CONTROL_MUTE_SELECTOR,
    );
    const goBackButtonNode = document.querySelector<HTMLButtonElement>(
        CONTROL_GO_BACK_SELECTOR,
    );
    const buttonContainerNode = document.querySelector<HTMLElement>(
        BUTTON_CONTAINER_SELECTOR,
    );
    const captionTextContainerNode = document.querySelector<HTMLElement>(
        CAPTION_TEXT_CONTAINER_SELECTOR,
    );
    const captionTextNode = document.querySelector<HTMLElement>(
        CAPTION_TEXT_SELECTOR,
    );
    const loaderContainerNode = document.querySelector<HTMLElement>(
        LOADER_CONTAINER_SELECTOR,
    );
    const replayContainerNode = document.querySelector<HTMLDivElement>(
        REPLAY_CONTAINER_SELECTOR,
    );
    const previewLabelNode = document.querySelector<HTMLDivElement>(
        PREVIEW_LABEL_SELECTOR,
    );
    const logoNode = document.querySelector<HTMLAnchorElement>(LOGO_SELECTOR);
    const brandSpaceNode =
        document.querySelector<HTMLDivElement>(BRAND_SPACE_SELECTOR);
    const footerNode = document.querySelector<HTMLElement>(FOOTER_SELECTOR);

    // NOTE: Nameplate nodes
    const nameplateContainerNode = document.querySelector<HTMLElement>(
        NAME_PLATE_CONTAINER_SELECTOR,
    );
    const nameplateContainerBlockNode = document.querySelector<HTMLElement>(
        NAME_PLATE_CONTAINER_BLOCK_SELECTOR,
    );
    const nameplateAuthorNode = document.querySelector<HTMLElement>(
        NAME_PLATE_AUTHOR_SELECTOR,
    );
    const nameplateRoleNode = document.querySelector<HTMLElement>(
        NAME_PLATE_ROLE_SELECTOR,
    );

    if (logoNode && workspace.url) {
        const utmQuery = `utm_medium=referral&utm_campaign=ReelFlowPlayer&utm_source=${encodeURIComponent(
            workspace.url,
        )}`;
        logoNode.href = `https://www.reelflow.com/powered-by?${utmQuery}`;
    }

    // NOTE: The script needs these nodes to work, so we check if they are all in place
    const uiNodes = [
        historyContainerNode,
        titleNode,
        videoPlaceholderNode,
        closeButtonNode,
        muteButtonNode,
        goBackButtonNode,
        buttonContainerNode,
        captionTextNode,
        loaderContainerNode,
        replayContainerNode,
        previewLabelNode,
        footerNode,
        brandSpaceNode,
    ];

    // NOTE: This condition looks better but TS doesn't understand it and shows 'possibly null' errors
    // NOTE: If at least one of mandatory nodes doesn't exist prevent page from being rendered
    // if (uiNodes.includes(null)) {
    //     return;
    // }

    if (
        !historyContainerNode ||
        !titleNode ||
        !badgeNode ||
        !videoPlaceholderNode ||
        !closeButtonNode ||
        !muteButtonNode ||
        !goBackButtonNode ||
        !buttonContainerNode ||
        !nameplateContainerBlockNode ||
        !captionTextContainerNode ||
        !captionTextNode ||
        !loaderContainerNode ||
        !replayContainerNode ||
        !footerNode ||
        !brandSpaceNode
    ) {
        logDebug("[ReelFlow] Mandatory node doesn't exist", uiNodes);

        return;
    }

    let buttonListShowAnimExecutor: Animation | null = null;

    const isBrandingHidden = flowConfigPlayer.brandingVariant === 4;

    const brandingVariant = flowConfigPlayer.brandingVariant || 1;

    if (isBrandingHidden) {
        brandSpaceNode.innerHTML = '';
        brandSpaceNode.classList.add('brand-space-hidden');
        footerNode.classList.add('footer__compacted');
    } else {
        if (brandingVariant === 1) {
            brandSpaceNode.classList.add('brand-space__variant-1');
        } else if (brandingVariant === 2) {
            brandSpaceNode.classList.add('brand-space__variant-2');
        }
    }

    Object.keys(flowConfigPlayer?.style?.footer ?? {}).forEach((styleKey) => {
        // @ts-expect-error
        footerNode.style[styleKey] = flowConfigPlayer.style.footer[styleKey];
    });

    // NOTE: Replacing the video placeholder with an actual video,
    //   so that we attach the listeners before rendering an element
    let videoNode = document.createElement('video');
    let trackNode = document.createElement('track');

    videoNode.classList.add('video-element');

    const soundPreferenceVariant = flowConfigPlayer.soundPreferenceVariant;
    const isSoundMutedByDefault =
        !soundPreferenceVariant || soundPreferenceVariant === 1;

    if (flowConfigPlayer.videos[0].webmUrl) {
        let webmSource = document.createElement('source');
        webmSource.src = flowConfigPlayer.videos[0].webmUrl;
        webmSource.type = 'video/webm; codecs=vp9';
        videoNode.appendChild(webmSource);
    }

    let mp4Source = document.createElement('source');
    mp4Source.src = flowConfigPlayer.videos[0].url;
    mp4Source.type = 'video/mp4';
    videoNode.appendChild(mp4Source);

    videoNode.role = 'video';
    videoNode.ariaLabel = 'Video Content';
    videoNode.crossOrigin = 'anonymous';
    videoNode.autoplay = false;
    videoNode.loop = false;
    videoNode.defaultMuted = isSoundMutedByDefault;
    videoNode.muted = isSoundMutedByDefault;
    videoNode.disablePictureInPicture = true;
    videoNode.disableRemotePlayback = true;
    videoNode.playsInline = true;
    videoNode.volume = 0.5;
    videoNode.playbackRate = flowConfigPlayer.videos[0].playbackRate || 1;

    Object.keys(flowConfigPlayer?.style?.video ?? {}).forEach((styleKey) => {
        // @ts-expect-error
        videoNode.style[styleKey] = flowConfigPlayer.style.video[styleKey];
    });

    trackNode.src = flowConfigPlayer.videos[0].subtitles;
    trackNode.label = 'English';
    trackNode.kind = 'subtitles';
    trackNode.srclang = 'en';
    trackNode.default = false;

    videoNode.appendChild(trackNode);

    //
    // NOTE: By default the element is hidden. Show only when the page is in preview mode
    //
    if (pageMetadata.isPreviewMode) {
        previewLabelNode?.classList.remove('hidden');
        brandSpaceNode.classList.add('hidden');
    }

    function onTrackCueChange(track: TextTrack) {
        if (!captionTextNode) {
            return;
        }

        if (
            track.activeCues &&
            track.activeCues.length > 0 &&
            // @ts-expect-error
            track.activeCues[0]?.text
        ) {
            if (!isCaptionTextVisible()) {
                showCaptionText();
            }

            // @ts-expect-error
            captionTextNode.innerHTML = track.activeCues[0].text;
        } else {
            hideCaptionText();
        }
    }

    function renderButton(button: FlowConfigPlayerVideoButton) {
        var elementType =
            button.actionType === OPEN_VIDEO_ACTION_TYPE ||
            button.actionType === HIDE_PLAYER_ACTION_TYPE
                ? 'button'
                : ['a', 'button', 'input'].includes(button.actionType)
                  ? button.actionType
                  : 'button';

        var element = document.createElement(elementType);

        element.classList.add('action-button');

        if (!button.style) {
            button.style = {};
        }

        // Combine styles for all buttons (i.e. "flowConfig.style.videoButton"), and
        //   styles defined for button individually (i.e. "button.style")
        var buttonStyle = Object.assign(
            {},
            flowConfigPlayer.style.videoButton,
            button.style,
        );

        if (buttonStyle) {
            Object.keys(buttonStyle).forEach(function (styleKey) {
                // @ts-expect-error
                element.style[styleKey] = buttonStyle[styleKey];
            });
        }

        if (
            button.actionType !== OPEN_VIDEO_ACTION_TYPE &&
            button.actionType !== HIDE_PLAYER_ACTION_TYPE
        ) {
            var customAttributes = ['hidePlayerOnNavigate'];

            Object.keys(button.actionPayload).forEach((attrName) => {
                if (customAttributes.includes(attrName)) {
                    return;
                }

                element.setAttribute(
                    attrName,
                    // @ts-expect-error
                    button.actionPayload[attrName],
                );
            });
        }

        element.setAttribute('data-action-id', button.id);

        element.innerHTML = button.label;

        buttonContainerNode?.append(element);
    }

    function renderHistoryBar() {
        var videoIds = flowState.videoIdHistory;
        var currentId = flowState.currentVideoId;

        var currentVideoData = flowConfigPlayer.videos.find(
            (videoItem) => videoItem.id === currentId,
        );

        if (!currentVideoData) {
            logDebug('[ReelFlow] Cannot get current video data', { currentId });

            return;
        }

        var currentHistoryContainer = document.querySelector(
            HISTORY_CONTAINER_SELECTOR,
        );

        if (currentHistoryContainer) {
            // currentHistoryContainer.remove();
        }

        if (!titleNode || !currentHistoryContainer) {
            return;
        }

        var historyElement = document.createElement('div');

        historyElement.classList.add('history-container');

        titleNode.innerHTML = matchTemplateStr(
            currentVideoData.title,
            pageMetadata.url,
        );

        var activeVideoIndex = videoIds.findIndex(
            (itemId) => itemId === currentId,
        );

        for (var i = 0; i < videoIds.length; i++) {
            var videoId = videoIds[i];
            var isFinished = i < activeVideoIndex;
            var isLast = i === videoIds.length - 1;
            var isActive = videoId === currentId;

            var historyItemElement = document.createElement('button');

            historyItemElement.classList.add('history-button');

            if (isLast) {
                historyItemElement.classList.add('history-button__last');
            }

            historyItemElement.role = 'slider';
            historyItemElement.ariaValueMin = '0';
            historyItemElement.ariaValueMax = '100';
            historyItemElement.ariaValueNow = isFinished ? '100' : '0';
            historyItemElement.ariaLabel = 'Video timeline';

            historyItemElement.setAttribute('data-video-id', videoId);

            var progressBg = document.createElement('div');

            progressBg.classList.add('history-button-progress-bg');

            if (isFinished) {
                progressBg.classList.add(
                    'history-button-progress-bg__finished',
                );
            }

            if (isActive) {
                var progressElement = document.createElement('div');

                progressElement.classList.add('history-button-progress-line');

                // TODO: Use CSS instead of JS re-renders

                progressBg.append(progressElement);
            }

            historyItemElement.append(progressBg);
            historyElement.append(historyItemElement);
        }

        currentHistoryContainer.replaceWith(historyElement);

        // There is no need to remove the previous event listener as
        //   the element it is attached to is removed from the DOM when the title is re-rendered.
        historyElement.addEventListener('click', function (event) {
            var targetButton = event.target;

            if (!targetButton) {
                return;
            }

            // @ts-expect-error
            var videoId = targetButton.getAttribute('data-video-id');

            if (
                !videoId
                // Clicking on the segment for the currently playing reel should restart that reel
                /* || videoId === flowState.currentVideoId */
            ) {
                return;
            }

            var videoData = flowConfigPlayer.videos.find(
                (item) => item.id === videoId,
            );

            if (!videoData) {
                return;
            }

            // @ts-expect-error
            var targetButtonRect = targetButton.getBoundingClientRect();

            var progress =
                (event.x - targetButtonRect.x) / targetButtonRect.width;

            if (videoId === flowState.currentVideoId) {
                if (!Number.isNaN(progress) && progress < 0.98) {
                    videoNode.currentTime = flowState.extra.duration * progress;

                    if (!flowState.controlMap.isPlaying) {
                        videoNode.muted = flowState.controlMap.isMuted;

                        // setMuteButtonIcon(videoNode.muted);

                        videoNode.play();
                    }
                }
            } else {
                replaceCurrentVideo(videoData);
            }
        });
    }

    function renderBadge(data: FlowConfigPlayerVideo) {
        if (
            !badgeInitialsTextNode ||
            !badgeExtraNameNode ||
            !badgeExtraRoleNode ||
            !badgeInitialsIconNode
        ) {
            return;
        }

        // Always show badge and enable hover functionality
        badgeNode?.classList.remove('hidden');
        badgeNode?.classList.remove('title-container__badge--no-hover');

        // Determine what author info is available
        const hasName = !!data?.authorName;
        const hasRole = !!data?.authorRole;
        const hasBothAuthorInfo = hasName && hasRole;

        if (hasBothAuthorInfo) {
            badgeInitialsIconNode?.classList.remove(
                'badge-initials-verified__centered',
            );
            badgeExtraNameNode.classList.add('badge-extra-name__bold');

            const [firstName, lastName] = data.authorName!.split(' ');

            badgeInitialsTextNode.innerHTML =
                firstName.charAt(0) + (lastName?.charAt(0) || '');

            badgeExtraNameNode.innerHTML = data.authorName!;
            badgeExtraRoleNode.innerHTML = data.authorRole!;

            // Show name and role divs when author info exists
            badgeExtraNameNode.classList.remove('hidden');
            badgeExtraRoleNode.classList.remove('hidden');

            // Remove name-only class when both name and role exist
            badgeExtraNameNode.classList.remove('name-only');

            // Make badge focusable
            badgeNode?.setAttribute('tabindex', '0');
            badgeNode?.setAttribute('role', 'button');
            badgeNode?.setAttribute(
                'aria-label',
                `${data.authorName!}, ${data.authorRole!}`,
            );

            if (!getIsNameViewed(data.authorName!, data.authorRole!)) {
                badgeNode?.classList.add('badge-animate-down');
                titleNode?.classList.add('title-animate-down');
            }
        } else if (hasName) {
            // Only name exists
            badgeInitialsIconNode?.classList.remove(
                'badge-initials-verified__centered',
            );
            badgeExtraNameNode.classList.add('badge-extra-name__bold');

            const [firstName, lastName] = data.authorName!.split(' ');

            badgeInitialsTextNode.innerHTML =
                firstName.charAt(0) + (lastName?.charAt(0) || '');

            badgeExtraNameNode.innerHTML = data.authorName!;
            badgeExtraRoleNode.innerHTML = '';

            // Show name, hide role
            badgeExtraNameNode.classList.remove('hidden');
            badgeExtraRoleNode.classList.add('hidden');

            // Remove bottom margin when only name is present
            badgeExtraNameNode.classList.add('name-only');

            // Make badge focusable
            badgeNode?.setAttribute('tabindex', '0');
            badgeNode?.setAttribute('role', 'button');
            badgeNode?.setAttribute('aria-label', data.authorName!);
        } else if (hasRole) {
            // Only role exists
            badgeInitialsIconNode?.classList.add(
                'badge-initials-verified__centered',
            );
            badgeExtraNameNode.classList.remove('badge-extra-name__bold');

            badgeInitialsTextNode.innerHTML = '';
            badgeExtraNameNode.innerHTML = '';
            badgeExtraRoleNode.innerHTML = data.authorRole!;

            // Hide name, show role
            badgeExtraNameNode.classList.add('hidden');
            badgeExtraRoleNode.classList.remove('hidden');

            // Remove name-only class when only role exists
            badgeExtraNameNode.classList.remove('name-only');

            // Make badge focusable
            badgeNode?.setAttribute('tabindex', '0');
            badgeNode?.setAttribute('role', 'button');
            badgeNode?.setAttribute('aria-label', data.authorRole!);
        } else {
            // No author info
            badgeInitialsIconNode?.classList.add(
                'badge-initials-verified__centered',
            );
            badgeExtraNameNode.classList.remove('badge-extra-name__bold');

            badgeInitialsTextNode.innerHTML = '';
            badgeExtraNameNode.innerHTML = '';
            badgeExtraRoleNode.innerHTML = '';

            // Hide name and role divs when no author info
            badgeExtraNameNode.classList.add('hidden');
            badgeExtraRoleNode.classList.add('hidden');

            // Remove name-only class when no author info
            badgeExtraNameNode.classList.remove('name-only');

            // Make badge focusable even without author info
            badgeNode?.setAttribute('tabindex', '0');
            badgeNode?.setAttribute('role', 'button');
            badgeNode?.setAttribute('aria-label', 'Content information');
        }

        // Handle content variant display in extra block
        if (badgeExtraContentVariantNode && badgeExtraNode) {
            const contentVariantText = getContentVariantText(
                data.contentVariant,
            );

            // Remove all height animation classes first
            badgeExtraNode.classList.remove(
                'title-container__badge-extra--with-content-variant',
                'title-container__badge-extra--without-content-variant',
                'title-container__badge-extra--content-variant-only',
                'title-container__badge-extra--name-only',
                'title-container__badge-extra--role-only',
            );

            if (contentVariantText) {
                badgeExtraContentVariantNode.innerHTML = contentVariantText;
                badgeExtraContentVariantNode.classList.remove('hidden');

                // Determine which height class to use based on author info availability
                if (hasBothAuthorInfo) {
                    // Has both author info - use the full height class
                    badgeExtraNode.classList.add(
                        'title-container__badge-extra--with-content-variant',
                    );
                    // Remove content-variant-only styling
                    badgeExtraContentVariantNode.classList.remove(
                        'content-variant-only',
                    );
                } else if (hasName) {
                    // Only name exists - use name-only class (48px height)
                    badgeExtraNode.classList.add(
                        'title-container__badge-extra--name-only',
                    );
                    // Remove content-variant-only styling (keep 12px font)
                    badgeExtraContentVariantNode.classList.remove(
                        'content-variant-only',
                    );
                } else if (hasRole) {
                    // Only role exists - use role-only class (48px height)
                    badgeExtraNode.classList.add(
                        'title-container__badge-extra--role-only',
                    );
                    // Remove content-variant-only styling (keep 12px font)
                    badgeExtraContentVariantNode.classList.remove(
                        'content-variant-only',
                    );
                } else {
                    // No author info - use content-variant-only class (48px height)
                    badgeExtraNode.classList.add(
                        'title-container__badge-extra--content-variant-only',
                    );
                    // Apply content-variant-only styling (14px font, no margin-top)
                    badgeExtraContentVariantNode.classList.add(
                        'content-variant-only',
                    );
                }
            } else {
                badgeExtraContentVariantNode.classList.add('hidden');
                // Remove content-variant-only styling when no content variant text
                badgeExtraContentVariantNode.classList.remove(
                    'content-variant-only',
                );

                // Only add height animation class if contentVariant is provided but has no text
                // Don't add any height classes if contentVariant is undefined
                if (data.contentVariant) {
                    badgeExtraNode.classList.add(
                        'title-container__badge-extra--without-content-variant',
                    );
                }
                // If contentVariant is undefined, no height animation classes are applied
            }
        }

        // Set appropriate badge icon based on content variant
        const hasAuthorInfo = !!(data?.authorName && data?.authorRole);
        setBadgeIcon(
            data.contentVariant,
            badgeInitialsIconNode,
            badgeIconVerifiedNode,
            badgeIconInfoNode,
            badgeIconQuestionNode,
            hasAuthorInfo,
        );
    }

    function replaceCurrentVideo(data: FlowConfigPlayerVideo) {
        var videoId = data.id;
        var mediaUrl = data.url;
        var subtitlesUrl = data.subtitles;
        var buttons = data.buttons;
        var form = data.form;
        var playbackRate = data.playbackRate || 1;
        const buttonPresentationDelay = data.buttonPresentationDelay;

        if (!videoNode || !buttonContainerNode || !replayContainerNode) {
            return;
        }

        // NOTE: Stop tracking watch time for current video before switching
        if (
            typeof videoNode.currentTime === 'number' &&
            videoWatchTimeState[flowConfig.code].isCurrentlyTracking
        ) {
            stopWatchTimeTracking(
                flowState.currentVideoId,
                videoNode.currentTime,
            );
        }

        videoNode.innerHTML = '';

        // NOTE: Reset the flag for new video or restart
        isVideoStartedTracked = false;

        if (data.webmUrl) {
            let webmSource = document.createElement('source');
            webmSource.src = data.webmUrl;
            webmSource.type = 'video/webm; codecs=vp9';
            videoNode.appendChild(webmSource);
        }

        let mp4Source = document.createElement('source');
        mp4Source.src = mediaUrl;
        mp4Source.type = 'video/mp4';
        videoNode.appendChild(mp4Source);

        videoNode.load();
        videoNode.currentTime = 0;

        buttonContainerNode
            .querySelectorAll(
                '.action-button, .action-form-input, .action-form-block',
            )
            .forEach((element) => {
                element.remove();
            });

        // NOTE: If there is a button presentation delay,
        //   hide the buttons container to animate it later
        if (buttonPresentationDelay || getIsNameplateAvailable(videoId)) {
            // buttonContainerNode.style.transition = 'unset';
            buttonContainerNode.style.opacity = '0';

            if (captionTextContainerNode) {
                captionTextContainerNode.style.transition = 'unset';
            }
        }

        buttons.forEach(renderButton);

        if (
            (buttonPresentationDelay || getIsNameplateAvailable(videoId)) &&
            captionTextContainerNode &&
            nameplateContainerBlockNode
        ) {
            nameplateContainerBlockNode.style.transform = `translateY(${getNameplateContainerVerticalOffset()}px)`;
            captionTextContainerNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
        }

        if (form) {
            // TODO: Render form
        }

        var nextTrackNode = document.createElement('track');

        nextTrackNode.src = subtitlesUrl;
        nextTrackNode.label = 'English';
        nextTrackNode.kind = 'subtitles';
        nextTrackNode.srclang = 'en';
        nextTrackNode.default = false;

        videoNode.appendChild(nextTrackNode);

        const nextTextTrack = videoNode.textTracks[0];

        nextTextTrack.mode = 'hidden';

        nextTextTrack.oncuechange = function () {
            onTrackCueChange(nextTextTrack);
        };

        flowState.currentVideoId = videoId;
        flowState.extra.isButtonListAnimated = false;

        if (!flowState.videoIdHistory.includes(videoId)) {
            flowState.videoIdHistory.push(videoId);
        }

        hideReplay();

        // clearTimeout(repeatTimeout);

        videoNode.play();

        flowState.controlMap.isPlaying = true;
        videoNode.muted = flowState.controlMap.isMuted;

        videoNode.playbackRate = playbackRate;

        renderHistoryBar();

        if (flowConfig.type === 'inline') {
            onResizePlayer();
        }

        // if (
        //     flowConfigPlayer.themeVariant === 2 ||
        //     (flowConfigPlayer.themeVariant === 3 && getIsDark())
        // ) {
        //     changeColorTheme(2);
        // }

        renderBadge(data);
    }

    function setMuteButtonIcon(isMuted: boolean) {
        if (!muteButtonNode) {
            return;
        }

        const isTooltipsEnabled = false;

        muteButtonNode.setAttribute('data-muted', `${isMuted}`);
        muteButtonNode.setAttribute('aria-pressed', `${isMuted}`);
        muteButtonNode.setAttribute(
            'aria-label',
            isMuted ? 'Unmute video' : 'Mute video',
        );

        let muteButtonIcon = isMuted
            ? getIcon('mute', { title: '', color: 'white' })
            : getIcon('unmute', { title: '', color: 'white' });

        muteButtonIcon = `<div class="control-button-content">${muteButtonIcon}</div>`;

        var isCompact = flowConfigPlayer.sizeVariant === 1;

        // TODO: Define as an element not inline HTML
        var tooltipRawHtml = `<div class="mute-tooltip" style="${
            isCompact ? 'font-size:12px' : ''
        }">${isMuted ? 'Turn sound on' : 'Turn sound off'}</div>`;

        muteButtonNode.innerHTML =
            muteButtonIcon + (isTooltipsEnabled ? tooltipRawHtml : '');
    }

    function showLoader() {
        if (loaderContainerNode) {
            loaderContainerNode.style.display = 'flex';
            loaderContainerNode.style.visibility = 'visible';
        }
    }

    function hideLoader() {
        if (loaderContainerNode) {
            loaderContainerNode.style.display = 'none';
            loaderContainerNode.style.visibility = 'hidden';
        }
    }

    function showReplay(isDisabledPointerEvents = false) {
        if (!replayContainerNode) {
            return;
        }

        replayContainerNode.style.display = 'flex';
        replayContainerNode.style.visibility = 'visible';
        replayContainerNode.tabIndex = 0;

        if (isDisabledPointerEvents) {
            replayContainerNode.style.pointerEvents = 'none';
        }
    }

    function hideReplay() {
        if (!replayContainerNode) {
            return;
        }

        replayContainerNode.style.display = 'none';
        replayContainerNode.style.visibility = 'hidden';
        replayContainerNode.tabIndex = 1;
        replayContainerNode.style.pointerEvents = 'auto';
    }

    function replayVideo(event: MouseEvent) {
        var currentVideoConfig = flowConfigPlayer.videos.find(function (data) {
            return data.id === flowState.currentVideoId;
        });

        var playbackRate =
            currentVideoConfig && currentVideoConfig.playbackRate
                ? currentVideoConfig.playbackRate
                : 1;

        if (!videoNode) {
            return;
        }

        if (
            !flowState.controlMap.isPlaying &&
            flowState.extra.currentTime &&
            flowState.extra.duration &&
            flowState.extra.currentTime === flowState.extra.duration
        ) {
            eventTracker.track('player_video_replay');

            hideReplay();

            videoNode.currentTime = 0;
            videoNode.muted = flowState.controlMap.isMuted;
            videoNode.play();
            videoNode.playbackRate = playbackRate;

            flowState.controlMap.isPlaying = true;
            flowState.controlMap.isMuted = !!videoNode.muted;

            isMuteStateChangedMap[flowConfig.code] = true;

            setMuteButtonIcon(videoNode.muted);
            cachePlayerMuteState();
        } else if (
            (!flowState.controlMap.isPlaying &&
                flowState.extra.currentTime &&
                flowState.extra.duration &&
                flowState.extra.currentTime !== flowState.extra.duration) ||
            (!flowState.controlMap.isPlaying &&
                !flowState.extra.currentTime &&
                !flowState.extra.duration &&
                flowConfig.type === 'inline')
        ) {
            eventTracker.track('player_play', { source: 'button' });

            if (isMuteStateChangedMap[flowConfig.code]) {
                videoNode.muted = flowState.controlMap.isMuted;
            } else {
                videoNode.muted = isSoundMutedByDefault;

                flowState.controlMap.isMuted = isSoundMutedByDefault;
            }

            videoNode.play();

            videoNode.playbackRate = playbackRate;

            hideReplay();

            flowState.controlMap.isPlaying = true;

            setMuteButtonIcon(videoNode.muted);
        }
    }

    function playVideo() {
        const currentVideoConfig = getCurrentVideoConfig();

        videoNode.playbackRate =
            currentVideoConfig && currentVideoConfig.playbackRate
                ? currentVideoConfig.playbackRate
                : 1;

        function onVideoPlayStart() {
            flowState.controlMap.isPlaying = true;

            if (isMuteStateChangedMap[flowConfig.code]) {
                videoNode.muted = flowState.controlMap.isMuted;
            } else {
                videoNode.muted = isSoundMutedByDefault;

                flowState.controlMap.isMuted = isSoundMutedByDefault;
            }

            setMuteButtonIcon(videoNode.muted);
        }

        videoNode
            .play()
            .then(onVideoPlayStart)
            .catch(function (ex) {
                // NOTE: The ".play" method can throw an error (mostly in Safari)
                //   Example: "NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission."
                logDebug(`[ReelFlow][playVideo] Cannot play video`, {
                    videoNode,
                    error: ex,
                });

                // NOTE: Sometimes autoplay fails due to mute state preferences, so attempt to play with sound off
                //   See more in ENG-1323 on Notion

                videoNode.muted = true;

                videoNode
                    .play()
                    .then(onVideoPlayStart)
                    .catch(function (ex) {
                        logDebug(
                            `[ReelFlow][playVideo] Cannot play video with sound off`,
                            {
                                videoNode,
                                error: ex,
                            },
                        );

                        showReplay(true);
                    });
            });
    }

    function onVideoClick(source?: 'keyboard') {
        if (!videoNode) {
            return;
        }

        if (flowState.controlMap.isPlaying) {
            eventTracker.track(
                'player_pause',
                source ? { source: source } : undefined,
            );

            videoNode.pause();

            flowState.controlMap.isPlaying = false;
        } else {
            eventTracker.track(
                'player_play',
                source ? { source: source } : undefined,
            );

            videoNode.muted = isMuteStateChangedMap[flowConfig.code]
                ? flowState.controlMap.isMuted
                : false;

            videoNode.play();

            const currentVideoConfig = flowConfigPlayer.videos.find(
                function (data) {
                    return data.id === flowState.currentVideoId;
                },
            );

            const playbackRate =
                currentVideoConfig && currentVideoConfig.playbackRate
                    ? currentVideoConfig.playbackRate
                    : 1;

            videoNode.playbackRate = playbackRate;

            flowState.controlMap.isPlaying = true;

            setMuteButtonIcon(videoNode.muted);
        }
    }

    function getCaptionTextVerticalOffset() {
        if (!buttonContainerNode) {
            return 0;
        }

        const buttonHeightPx = 41;

        const childElementCount =
            flowConfig.type === 'inline'
                ? 1
                : buttonContainerNode.children.length;

        return childElementCount * buttonHeightPx;
    }

    function getNameplateContainerVerticalOffset() {
        const captionTextVerticalOffset = getCaptionTextVerticalOffset();

        if (!captionTextVerticalOffset) {
            return 0;
        }

        return captionTextVerticalOffset - 16;
    }

    function getIsNameplateAvailable(targetVideoId?: string) {
        if (!targetVideoId) {
            targetVideoId = flowState.currentVideoId;
        }

        const currentVideo = flowConfigPlayer.videos.find(
            (video) => video.id === targetVideoId,
        );

        return !!(
            currentVideo?.authorName &&
            currentVideo?.authorRole &&
            !getIsNameViewed(currentVideo.authorName, currentVideo.authorRole)
        );
    }

    function getButtonAnimationDelayInSeconds() {
        const currentVideo = flowConfigPlayer.videos.find(
            (video) => video.id === flowState.currentVideoId,
        );

        const buttonPresentationDelay = currentVideo?.buttonPresentationDelay;

        const videoDuration = videoNode.duration;

        let animationDelay = 0;

        if (
            typeof buttonPresentationDelay === 'number' &&
            buttonPresentationDelay !== 0
        ) {
            if (buttonPresentationDelay > 0) {
                animationDelay = buttonPresentationDelay;

                if (animationDelay > videoDuration) {
                    animationDelay = videoDuration;
                }
            } else {
                animationDelay = videoDuration + buttonPresentationDelay;

                if (animationDelay < 0) {
                    animationDelay = videoDuration;
                }
            }
        }

        // NOTE: Buttons, should always be animated after nameplate animation
        const nameplateAnimationDelayInSeconds = (() => {
            if (getIsNameplateAvailable()) {
                return 3.5;
            }

            return 0;
        })();

        animationDelay += nameplateAnimationDelayInSeconds;

        return animationDelay;
    }

    function getIsReadyToAnimateButtonList(currentTime: number) {
        const currentVideo = flowConfigPlayer.videos.find(
            (video) => video.id === flowState.currentVideoId,
        );

        const buttonPresentationDelay = currentVideo?.buttonPresentationDelay;

        if (buttonPresentationDelay || getIsNameplateAvailable()) {
            const animDelay = getButtonAnimationDelayInSeconds();

            return currentTime >= animDelay;
        } else {
            // NOTE: Animate immediately if no value provided AND nameplate is not available
            return true;
        }
    }

    function onButtonListAnimationFinish() {
        if (!buttonContainerNode || !captionTextContainerNode) {
            return;
        }

        buttonContainerNode.style.opacity = '1';
        buttonContainerNode.style.pointerEvents = 'auto';

        // NOTE: Move captions back to normal position
        captionTextContainerNode.style.transform = 'translateY(0)';

        buttonListShowAnimExecutor = null;
    }

    function resetButtonListAnimation() {
        if (
            !buttonContainerNode ||
            !captionTextContainerNode ||
            !nameplateContainerBlockNode
        ) {
            return;
        }

        buttonContainerNode.style.opacity = '0';
        buttonContainerNode.style.pointerEvents = 'none';

        if (getButtonAnimationDelayInSeconds() > 0) {
            nameplateContainerBlockNode.style.transform = `translateY(${getNameplateContainerVerticalOffset()}px)`;
            captionTextContainerNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
        } else {
            captionTextContainerNode.style.transform = `0`;
        }

        buttonListShowAnimExecutor = null;
    }

    function animateButtonList(videoDuration: number) {
        const currentVideo = flowConfigPlayer.videos.find(
            (video) => video.id === flowState.currentVideoId,
        );

        const buttonPresentationDelay = currentVideo?.buttonPresentationDelay;

        function _animateButtonList(buttonPresentationDelay?: number) {
            if (
                !buttonContainerNode ||
                !captionTextContainerNode ||
                !nameplateContainerBlockNode
            ) {
                return;
            }

            // NOTE: Initially hide buttons and shift captions down
            captionTextContainerNode.style.transition =
                'transform, opacity 0.18s ease-in';
            buttonContainerNode.style.opacity = '0';
            buttonContainerNode.style.pointerEvents = 'none';

            if (buttonPresentationDelay || getIsNameplateAvailable()) {
                nameplateContainerBlockNode.style.transform = `translateY(${getNameplateContainerVerticalOffset()}px)`;
                captionTextContainerNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
            }

            buttonListShowAnimExecutor = buttonContainerNode.animate(
                {
                    opacity: [0, 1],
                },
                {
                    duration: 300,
                    iterations: 1,
                },
            );

            buttonListShowAnimExecutor.addEventListener(
                'finish',
                onButtonListAnimationFinish,
            );

            buttonListShowAnimExecutor.addEventListener(
                'cancel',
                onButtonListAnimationFinish,
            );
        }

        if (buttonListShowAnimExecutor) {
            // NOTE: Cancel any ongoing animation
            buttonListShowAnimExecutor.cancel();
        }

        _animateButtonList(buttonPresentationDelay);
    }

    function animateNamePlateContainer(payload: {
        authorName: string;
        authorRole: string;
    }) {
        const authorName = payload.authorName;
        const authorRole = payload.authorRole;

        if (
            !buttonContainerNode ||
            !badgeNode ||
            !captionTextNode ||
            !nameplateAuthorNode ||
            !nameplateRoleNode ||
            !nameplateContainerNode
        ) {
            return;
        }

        // If name hasn't been viewed yet, show the extra container
        if (
            authorName &&
            authorRole &&
            !getIsNameViewed(authorName, authorRole)
        ) {
            nameplateAuthorNode.innerHTML = authorName;
            nameplateRoleNode.innerHTML = authorRole;

            buttonContainerNode.style.pointerEvents = 'none';

            // NOTE: Add class to trigger the extra container animation
            nameplateContainerNode.classList.add('animate-up');

            // NOTE: Set a timeout to remove the class after animation completes
            setTimeout(() => {
                nameplateContainerNode?.classList.remove('animate-up');
                badgeNode?.classList.remove('badge-animate-down');
                titleNode?.classList.remove('title-animate-down');

                buttonContainerNode.style.pointerEvents = 'auto';

                setIsNameViewed(authorName, authorRole);
            }, 3000);
        }
    }

    function onResizePlayer() {
        const buttonListEl = document.querySelector<HTMLDivElement>(
            BUTTON_CONTAINER_SELECTOR,
        );

        if (!buttonListEl) {
            return;
        }

        const buttonListElCopy = buttonListEl.cloneNode(true) as HTMLDivElement;

        if (!buttonListEl.classList.contains('wrapped-content')) {
            if (getIsContentWrapped(buttonListEl)) {
                buttonListEl.classList.add('wrapped-content');
            }
        } else {
            buttonListElCopy.classList.remove('wrapped-content');
            buttonListElCopy.style.opacity = '0';
            buttonListElCopy.style.zIndex = '-1';

            document.body.append(buttonListElCopy);

            if (!getIsContentWrapped(buttonListElCopy)) {
                buttonListEl.classList.remove('wrapped-content');
            }

            buttonListElCopy.remove();
        }
    }

    function onIntersectionChange(
        payload: PlayerIntersectionChangeMessagePayload,
    ) {
        if (
            (payload.isIntersecting && flowState.controlMap.isPlaying) ||
            (!payload.isIntersecting && !flowState.controlMap.isPlaying)
        ) {
            return;
        }

        const isPauseVideoOnIntersectionLossEnabled = false;
        const isAutoPlayEnabled =
            flowConfigPlayer.autoPlay || !('autoPlay' in flowConfigPlayer);

        if (
            payload.isIntersecting &&
            !flowState.controlMap.isPlaying &&
            isAutoPlayEnabled &&
            //
            // NOTE: Player should not auto-play more than once during the page view
            //
            // NOTE: [ChatGPT]
            // Chrome may set "video.currentTime" slightly above 0 (e.g., 0.007) when playback starts due to:
            //   - Minor timing variances in buffering and decoding
            //   - Floating-point precision limits in JavaScript
            // Using a small threshold (e.g., currentTime < 1) handles these discrepancies across browsers.
            //
            flowState.extra.currentTime < 1
        ) {
            if (
                flowConfigPlayer.autoPlayDelay &&
                flowConfigPlayer.autoPlayDelay > 0
            ) {
                const timerId = setTimeout(
                    playVideo,
                    flowConfigPlayer.autoPlayDelay * 1000,
                );

                autoPlayerTimers.push(timerId);
            } else {
                playVideo();
            }
        } else if (
            !payload.isIntersecting &&
            flowState.controlMap.isPlaying &&
            isPauseVideoOnIntersectionLossEnabled
        ) {
            videoNode.pause();

            autoPlayerTimers.forEach((timerId) => {
                clearTimeout(timerId);
            });

            autoPlayerTimers = [];
        }
    }

    function getCurrentVideoConfig() {
        var currentVideoConfig = flowConfigPlayer.videos.find(function (data) {
            return data.id === flowState.currentVideoId;
        });

        return currentVideoConfig;
    }

    function onVideoClose(source: 'button' | 'keyboard' | 'api') {
        // NOTE: Stop tracking watch time when player closes
        if (typeof videoNode.currentTime === 'number') {
            stopWatchTimeTracking(
                flowState.currentVideoId,
                videoNode.currentTime,
            );
        }

        eventTracker.track('player_close', { source });

        postMessage(PLAYER_CLOSE_MESSAGE_TYPE);

        videoNode.pause();

        videoNode.currentTime = 0;

        // NOTE: Reset the flag for new video or restart
        isVideoStartedTracked = false;

        flowState.extra.isButtonListAnimated = false;

        resetButtonListAnimation();
    }

    function showCaptionText() {
        if (!captionTextNode) {
            return;
        }

        captionTextNode.style.opacity = '1';
        captionTextNode.style.pointerEvents = 'auto';
    }

    function hideCaptionText() {
        if (!captionTextNode) {
            return;
        }

        captionTextNode.style.opacity = '0';
        captionTextNode.style.pointerEvents = 'none';
    }

    function isCaptionTextVisible() {
        return (
            captionTextNode?.style.opacity === '1' &&
            captionTextNode?.style.pointerEvents === 'auto'
        );
    }

    var currentVideo = flowConfigPlayer.videos.find(function (video) {
        return video.id === flowState.currentVideoId;
    }) as FlowConfigPlayerVideo;

    titleNode.innerHTML = matchTemplateStr(
        currentVideo.title,
        pageMetadata.url,
    );

    currentVideo.buttons.forEach(renderButton);

    // NOTE: This is already done in "replaceCurrentVideo" function, but we don't call it for the first player open
    if (currentVideo.buttonPresentationDelay || getIsNameplateAvailable()) {
        nameplateContainerBlockNode.style.transform = `translateY(${getNameplateContainerVerticalOffset()}px)`;
        captionTextContainerNode.style.transform = `translateY(${getCaptionTextVerticalOffset()}px)`;
    }

    renderHistoryBar();
    // replaceCurrentVideo(currentVideo);
    renderBadge(currentVideo);

    const videoTextTrack = videoNode.textTracks[0];

    videoTextTrack.mode = 'hidden';

    videoTextTrack.oncuechange = function () {
        onTrackCueChange(videoTextTrack);
    };

    videoNode.ontimeupdate = function () {
        if (!videoNode) {
            return;
        }

        if (typeof videoNode.currentTime === 'number') {
            flowState.extra.currentTime = videoNode.currentTime;
        }

        if (typeof videoNode.duration === 'number') {
            flowState.extra.duration = videoNode.duration;
        }

        // NOTE: Update watch time if video is playing
        if (
            flowState.controlMap.isPlaying &&
            typeof videoNode.currentTime === 'number'
        ) {
            updateWatchTime(flowState.currentVideoId, videoNode.currentTime);

            const currentTimeMs = Date.now();

            if (
                currentTimeMs -
                    videoWatchTimeState[flowConfig.code]
                        .lastAnalyticsEventTrackedAt >
                1000
            ) {
                eventTracker.trackLatest('player_flow_video_watchtime', {
                    flow_id: flowConfig.code,
                    video_id: flowState.currentVideoId,
                    watch_time_ms: getVideoWatchTimeMs(
                        flowState.currentVideoId,
                    ),
                });

                videoWatchTimeState[
                    flowConfig.code
                ].lastAnalyticsEventTrackedAt = currentTimeMs;
            }
        }

        if (
            !flowState.extra.isButtonListAnimated &&
            getIsReadyToAnimateButtonList(videoNode.currentTime)
        ) {
            animateButtonList(videoNode.duration);

            flowState.extra.isButtonListAnimated = true;
        }

        if (!flowState.controlMap.isPlaying) {
            return;
        }

        if (
            videoNode &&
            muteButtonNode.getAttribute('data-muted') !== `${videoNode.muted}`
        ) {
            setMuteButtonIcon(videoNode.muted);
        }

        var headerElement = document.querySelector(HISTORY_CONTAINER_SELECTOR);

        var historyActiveButtonElement = headerElement?.querySelector(
            'button[data-video-id="' + flowState.currentVideoId + '"]',
        );

        if (!historyActiveButtonElement) {
            return null;
        }

        historyActiveButtonElement.setAttribute(
            'aria-valuenow',
            ((videoNode.currentTime / videoNode.duration) * 100).toFixed(2),
        );

        var progressBarElement =
            historyActiveButtonElement.querySelector<HTMLDivElement>(
                '.history-button-progress-line',
            );

        var videoProgress = (videoNode.currentTime / videoNode.duration) * 100;

        if (progressBarElement) {
            progressBarElement.style.transform =
                'translateX(-' + (100 - videoProgress) + '%)';
        }
    };

    // NOTE: The event fires whenever a new video file is loaded into the player
    videoNode.onloadedmetadata = function () {
        // NOTE: Reset the flag for new video or restart
        isVideoStartedTracked = false;
    };

    videoNode.onwaiting = function (event) {
        flowState.controlMap.isPlaying = false;

        // NOTE: Stop tracking watch time while video is buffering
        if (typeof videoNode.currentTime === 'number') {
            stopWatchTimeTracking(
                flowState.currentVideoId,
                videoNode.currentTime,
            );
        }

        showLoader();
    };

    videoNode.onplaying = function (event) {
        flowState.controlMap.isPlaying = true;

        // NOTE: Start tracking watch time when video starts playing
        if (typeof videoNode.currentTime === 'number') {
            startWatchTimeTracking(
                flowState.currentVideoId,
                videoNode.currentTime,
            );
        }

        hideLoader();

        //
        // NOTE: For some reason in Chrome, when switching between videos (e.g. open video by clicking action button),
        //   the "currentTime" value is slightly above 0 (e.g. 0.007242, 0.009947, etc.). Because of this, we add a
        //   threshold of 1 seconds in which we consider the video "_started" playing and the rest is "_resumed".
        //
        // NOTE: [ChatGPT]
        // Chrome may set "video.currentTime" slightly above 0 (e.g., 0.007) when playback starts due to:
        //   - Minor timing variances in buffering and decoding
        //   - Floating-point precision limits in JavaScript
        // Using a small threshold (e.g., currentTime < 0.1) handles these discrepancies across browsers.
        //
        if (!isVideoStartedTracked && videoNode.currentTime < 1) {
            isVideoStartedTracked = true;

            eventTracker.track('player_video_started', {
                video_id: flowState.currentVideoId,
                current_time: videoNode.currentTime,
            });
        }
    };

    videoNode.onpause = function () {
        hideCaptionText();

        // NOTE: Stop tracking watch time when video pauses
        if (typeof videoNode.currentTime === 'number') {
            stopWatchTimeTracking(
                flowState.currentVideoId,
                videoNode.currentTime,
            );
        }

        showReplay();

        flowState.controlMap.isPlaying = false;
    };

    videoNode.onplay = function (event) {
        showCaptionText();

        hideReplay();

        flowState.controlMap.isPlaying = true;

        var targetVideo = event.target as HTMLVideoElement;

        var isVideoStartEvent =
            !isVideoStartedTracked && videoNode.currentTime < 1;

        if (!isVideoStartEvent) {
            eventTracker.track('player_video_resumed', {
                video_id: flowState.currentVideoId,
                current_time: videoNode.currentTime,
            });
        }

        // In Chrome (and perhaps other browsers), if video is muted, and browser windows loses focus,
        //   the browser is pausing video, so when user focuses browser window again the video resumes,
        //   and "play" event gets called again. Because of that we have to check "currentTime" before animation
        if (
            targetVideo &&
            typeof targetVideo.currentTime === 'number' &&
            targetVideo.currentTime < 1
        ) {
            var currentVideo = flowConfigPlayer.videos.find(
                (el) => el.id === flowState.currentVideoId,
            );

            if (!currentVideo) {
                return;
            }

            animateNamePlateContainer({
                authorName: currentVideo.authorName || '',
                authorRole: currentVideo.authorRole || '',
            });

            if (
                !flowState.extra.isButtonListAnimated &&
                getIsReadyToAnimateButtonList(videoNode.currentTime)
            ) {
                animateButtonList(videoNode.duration);

                flowState.extra.isButtonListAnimated = true;
            }
        }
    };

    videoNode.onclick = function () {
        if (!isVideoMouseDownEffectRunning) {
            onVideoClick();
        } else {
            isVideoMouseDownEffectRunning = false;
        }
    };

    videoNode.onended = function () {
        // NOTE: Stop tracking watch time when video ends
        if (typeof videoNode.currentTime === 'number') {
            stopWatchTimeTracking(
                flowState.currentVideoId,
                videoNode.currentTime,
            );
        }

        eventTracker.track('player_video_ended', {
            _payloadVersion: 2,
            video_id: flowState.currentVideoId,
            current_time: videoNode.currentTime,
        });

        if (flowState.extra.currentTime !== flowState.extra.duration) {
            // Set current time to duration, so we can identify the state (ended)
            flowState.extra.currentTime = flowState.extra.duration;
        }

        flowState.controlMap.isPlaying = false;

        videoNode.muted = true; // Mute at end of playback to avoid browser autoplay restrictions and annoying the user

        showReplay();
    };

    // NOTE: This event will not fire in mobile/tablet devices if data-saver is on in browser settings
    // See more: https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/loadeddata_event
    videoNode.onloadeddata = function () {
        eventTracker.track('player_video_loaded', {
            _payloadVersion: 2,
            video_id: flowState.currentVideoId,
        });
    };

    function startSpeedModeTimer() {
        if (videoMouseDownTimerId) {
            clearTimeout(videoMouseDownTimerId);

            videoMouseDownTimerId = null;
        }

        videoMouseDownTimerId = setTimeout(function () {
            videoMouseDownTimerId = null;
            isVideoMouseDownEffectRunning = true;

            const currentVideoConfig = getCurrentVideoConfig();
            const defaultPlaybackRate =
                currentVideoConfig && currentVideoConfig.playbackRate
                    ? currentVideoConfig.playbackRate
                    : 1;

            videoNode.playbackRate = defaultPlaybackRate * 2;

            hideElementsForSpeedMode();
        }, 250);
    }

    function stopSpeedModeTimer() {
        if (videoMouseDownTimerId) {
            clearTimeout(videoMouseDownTimerId);

            videoMouseDownTimerId = null;
        }

        const currentVideoConfig = getCurrentVideoConfig();
        const defaultPlaybackRate =
            currentVideoConfig && currentVideoConfig.playbackRate
                ? currentVideoConfig.playbackRate
                : 1;

        videoNode.playbackRate = defaultPlaybackRate;

        showElements();

        // NOTE: No need to reset the flag, because we do it in the "videoNode.onclick" handler
        // isVideoMouseDownEffectRunning = false;
    }

    videoNode.onmousedown = startSpeedModeTimer;
    videoNode.onmouseup = stopSpeedModeTimer;
    videoNode.ontouchstart = startSpeedModeTimer;
    videoNode.ontouchend = stopSpeedModeTimer;
    videoNode.onmouseleave = stopSpeedModeTimer;

    videoNode.onerror = function (event) {
        eventTracker.track('error', {
            name: 'player_video_load',
            // @ts-expect-error
            message: event.message || '',
        });
    };

    replayContainerNode.addEventListener('click', replayVideo);

    closeButtonNode.onclick = function (event) {
        onVideoClose('button');
    };

    muteButtonNode.onclick = function (event) {
        if (videoNode.muted) {
            eventTracker.track('player_unmute');
        } else {
            eventTracker.track('player_mute');
        }

        videoNode.muted = !videoNode.muted; // Toggle

        flowState.controlMap.isMuted = !!videoNode.muted;

        isMuteStateChangedMap[flowConfig.code] = true;

        setMuteButtonIcon(videoNode.muted);
        cachePlayerMuteState();
    };

    goBackButtonNode.onclick = function (event) {
        const currentVideoHistoryIndex = flowState.videoIdHistory.findIndex(
            (videoId) => videoId === flowState.currentVideoId,
        );

        const isFirstVideo = currentVideoHistoryIndex === 0;

        const isVideoFinished =
            flowState.extra.currentTime &&
            flowState.extra.duration &&
            flowState.extra.currentTime === flowState.extra.duration;

        let videoData = null;

        if (isFirstVideo || isVideoFinished) {
            videoData = flowConfigPlayer.videos.find(
                (item) => item.id === flowState.currentVideoId,
            );
        } else {
            // In case there is a previous video - choose it, otherwise use current video
            var targetVideoIndex =
                currentVideoHistoryIndex - 1 >= 0
                    ? currentVideoHistoryIndex - 1
                    : currentVideoHistoryIndex;

            videoData = flowConfigPlayer.videos[targetVideoIndex];
        }

        if (!videoData) {
            return;
        }

        eventTracker.track('player_go_back');

        replaceCurrentVideo(videoData);
    };

    buttonContainerNode.addEventListener('click', (event) => {
        if (!event || !event.target) {
            return;
        }

        // @ts-expect-error
        var actionId = event.target.getAttribute('data-action-id');
        // @ts-expect-error
        var label = event.target.innerHTML;

        var currentVideo = flowConfigPlayer.videos.find(
            (videoItem) => videoItem.id === flowState.currentVideoId,
        );

        if (!currentVideo) {
            // TODO: Log error
            // eventTracker.track("error", { /* ... */ });

            logDebug('[ReelFlow] Cannot find current video', {
                actionId,
                label,
            });

            return;
        }

        var buttonData = currentVideo.buttons.find(
            (buttonItem) => buttonItem.id === actionId,
        );

        var buttonIndex = currentVideo.buttons.findIndex(
            (buttonItem) => buttonItem.id === actionId,
        );

        if (!buttonData) {
            // TODO: Log error
            // eventTracker.track("error", { /* ... */});

            logDebug('[ReelFlow] Cannot find button data', {
                actionId,
                label,
                currentVideo,
            });

            return;
        }

        eventTracker.track('player_action_button', {
            _payloadVersion: 2,
            id: actionId,
            label,
            video_id: flowState.currentVideoId,
        });

        postMessage(ACTION_BUTTON_CLICK_MESSAGE_TYPE, {
            id: actionId,
            label,
            index: buttonIndex,
        });

        const triggerPlayerCloseAction = () => {
            closeButtonNode.click();
        };

        if (
            buttonData.actionType === 'a' &&
            buttonData.actionPayload &&
            buttonData.actionPayload.href &&
            typeof buttonData.actionPayload.href === 'string' &&
            buttonData.actionPayload.href.includes('#')
        ) {
            const targetPageUrl = new URL(buttonData.actionPayload.href);
            const currentPageUrl = new URL(pageMetadata.url);

            const currentPage =
                currentPageUrl.origin +
                currentPageUrl.pathname.replace(/\/$/, '');
            const targetPage =
                targetPageUrl.origin +
                targetPageUrl.pathname.replace(/\/$/, '');

            function checkNextVideoIdAndNavigateIfExists() {
                const nextVideoId = buttonData?.actionPayload?.videoId;

                if (nextVideoId) {
                    const targetVideo = flowConfigPlayer.videos.find(
                        (videoItem) => videoItem.id === nextVideoId,
                    );

                    if (targetVideo) {
                        replaceCurrentVideo(targetVideo);
                    }
                }
            }

            if (currentPage === targetPage) {
                const elementId = targetPageUrl.hash;

                if (!elementId) {
                    if (buttonData.actionPayload.hidePlayerOnNavigate) {
                        triggerPlayerCloseAction();
                    }

                    checkNextVideoIdAndNavigateIfExists();

                    return;
                }

                event.preventDefault();

                const payload: PageScrollMessagePayload = {
                    elementId,
                    // @ts-expect-error
                    scrollStateDelay: buttonData.actionPayload.scrollStateDelay,
                    // @ts-expect-error
                    scrollBehavior: buttonData.actionPayload.scrollBehavior,
                    // @ts-expect-error
                    scrollBlock: buttonData.actionPayload.scrollBlock,
                    // @ts-expect-error
                    scrollInline: buttonData.actionPayload.scrollInline,
                };

                postMessage(PAGE_SCROLL_REQUEST_MESSAGE_TYPE, payload);
            }

            checkNextVideoIdAndNavigateIfExists();

            if (buttonData.actionPayload.hidePlayerOnNavigate) {
                triggerPlayerCloseAction();
            }
        } else if (buttonData.actionType === OPEN_VIDEO_ACTION_TYPE) {
            var targetFlowId = buttonData.actionPayload.flowId;
            var targetVideoId = buttonData.actionPayload.videoId;

            var targetVideo = flowConfigPlayer.videos.find(
                (videoItem) => videoItem.id === targetVideoId,
            );

            if (targetVideo) {
                replaceCurrentVideo(targetVideo);
            }
        } else if (
            buttonData.actionType === HIDE_PLAYER_ACTION_TYPE ||
            (buttonData.actionPayload &&
                buttonData.actionPayload.hidePlayerOnNavigate)
        ) {
            triggerPlayerCloseAction();
        }
    });

    messageEventListenerMap[VIDEO_RENDER_LISTENER_ID] = function (event) {
        if (event.type === PLAYER_OPEN_MESSAGE_TYPE) {
            playVideo();
        } else if (event.type === METADATA_CHANGE_MESSAGE_TYPE) {
            if (flowConfig.type === 'inline') {
                // TODO: Check if the size has change (prev metadata to what received in the event)
                onResizePlayer();
            }
        } else if (event.type === PLAYER_INTERSECTION_CHANGE_MESSAGE_TYPE) {
            const payload =
                event.data as PlayerIntersectionChangeMessagePayload;

            onIntersectionChange(payload);
        } else if (event.type === PLAYER_CLOSE_REQUEST_MESSAGE_TYPE) {
            onVideoClose('api');
        }
    };

    keyDownListenerMap[ESCAPE_KEYBOARD_LISTENER_ID] = function (event) {
        onVideoClose('keyboard');
    };

    keyDownListenerMap[SPACE_KEYBOARD_LISTENER_ID] = function (event) {
        const isControlButtonFocused =
            document.activeElement === muteButtonNode ||
            document.activeElement === closeButtonNode;

        const isAnyHeaderButtonFocused = Array.from(
            document.querySelectorAll('.history-button'),
        ).some((button) => document.activeElement === button);

        const isAnyActionButtonFocused = Array.from(
            document.querySelectorAll('.action-button'),
        ).some((button) => document.activeElement === button);

        // If user clicks on the Space whilst other buttons are focused (controls, action buttons, etc.)
        //   no need to handle the action since most likely it should be handled automatically
        //   (e.g. clicking on close closes the player)
        if (
            isControlButtonFocused ||
            isAnyHeaderButtonFocused ||
            isAnyActionButtonFocused
        ) {
            return;
        } else {
            onVideoClick('keyboard');
        }
    };

    keyDownListenerMap[K_KEYBOARD_LISTENER_ID] = function (event) {
        onVideoClick('keyboard');
    };

    videoPlaceholderNode.replaceWith(videoNode);

    if (flowConfig.type === 'inline') {
        onResizePlayer();
        showReplay();
    }
}

function setupMessageListener() {
    window.addEventListener('message', function (event) {
        const workspaceUrl = new URL(workspace.url);

        const normalizedOrigin = normalizeUrl(event.origin);
        const normalizedWorkspaceOrigin = normalizeUrl(workspaceUrl.origin);

        if (normalizedOrigin !== normalizedWorkspaceOrigin) {
            logDebug('[ReelFlow] Unknown domain, ignore the message', {
                event,
                url: workspace.url,
                normalizedOrigin,
                normalizedWorkspaceOrigin,
            });

            console.error(
                'ReelFlow Install Code issue: the Workspace URL does not match this domain.',
            );

            const domainMismatchContainerNode =
                document.querySelector<HTMLElement>(
                    DOMAIN_MISMATCH_CONTAINER_SELECTOR,
                );

            // NOTE: Hide player UI for invalid domains
            domainMismatchContainerNode?.classList.remove('hidden');

            return;
        }

        // NOTE: The parent page (workspace URL) can send its own messages to our iframe,
        //   so if it's not a valid JSON (what we expect), ignore the message
        const eventData = (() => {
            try {
                return JSON.parse(event.data);
            } catch (ex) {
                logDebug('[ReelFlow] Error parsing message', { event });
            }

            return null;
        })();

        if (!eventData) {
            return;
        }

        logDebug('[ReelFlow] New message received', {
            event,
            payload: eventData,
        });

        messageHistory.push({ message: eventData, receivedAt: Date.now() });

        Object.keys(messageEventListenerMap).forEach((key) => {
            const onMessage = messageEventListenerMap[key as MessageListenerId];

            onMessage?.(eventData);
        });
    });
}

// NOTE: Get origin from page metadata URL or default to workspace URL.
//   Added as a part of a fix for: ENG-1120
function getTargetOrigin() {
    if (!pageMetadata || !pageMetadata.url) {
        logDebug('[ReelFlow][getTargetOrigin] Cannot get target origin', {
            pageMetadata,
        });

        return null;
    }

    const pageUrl = new URL(pageMetadata.url);

    return pageUrl.origin;
}

function postMessage(type: MessageType, data?: Message<any>['data']) {
    try {
        const payload = {
            type,
            data: {
                ...(data || {}),
                flowId: flowConfig.code,
            },
        };

        const targetOrigin = getTargetOrigin();

        if (!targetOrigin) {
            logDebug('[ReelFlow] Cannot post message', {
                type,
                data,
                targetOrigin,
            });

            return;
        }

        window.parent.postMessage(JSON.stringify(payload), targetOrigin);
    } catch (ex) {
        logDebug('[ReelFlow] Cannot post message', { type, data });
    }
}

function setupKeyDownListener() {
    document.addEventListener('keydown', function (event) {
        const listenerKeyCodeMap: Record<KeyDownListenerId, number | number[]> =
            {
                [ESCAPE_KEYBOARD_LISTENER_ID]: 27,
                [SPACE_KEYBOARD_LISTENER_ID]: 32,
                [K_KEYBOARD_LISTENER_ID]: 75,
            };

        Object.keys(listenerKeyCodeMap).forEach((id) => {
            const listenerId = id as KeyDownListenerId;

            if (
                typeof listenerKeyCodeMap[listenerId] === 'number' &&
                listenerKeyCodeMap[listenerId] === event.keyCode
            ) {
                keyDownListenerMap[listenerId]?.(event);
            }
        });
    });
}

function hideElementsForSpeedMode() {
    const elementsToHide = [
        document.querySelector(CONTROLS_CONTAINER_SELECTOR),
        document.querySelector(HISTORY_CONTAINER_SELECTOR),
        document.querySelector(TITLE_SELECTOR),
        document.querySelector(NAME_PLATE_CONTAINER_SELECTOR),
        document.querySelector(BADGE_SELECTOR),
        document.querySelector(BUTTON_CONTAINER_SELECTOR),
    ];

    elementsToHide.forEach((element) => {
        if (element) {
            element.classList.add('speed-mode-hidden');
        }
    });

    const speedIndicator = document.querySelector(SPEED_INDICATOR_SELECTOR);

    if (speedIndicator) {
        speedIndicator.classList.remove('hidden');
    }

    playerStateMap[flowConfig.code].extra.uiElementsHidden = true;
}

function showElements() {
    if (!playerStateMap[flowConfig.code].extra.uiElementsHidden) {
        return;
    }

    const speedIndicator = document.querySelector(SPEED_INDICATOR_SELECTOR);

    if (speedIndicator) {
        speedIndicator.classList.add('hidden');
    }

    document.querySelectorAll('.speed-mode-hidden').forEach((element) => {
        element.classList.remove('speed-mode-hidden');
    });

    playerStateMap[flowConfig.code].extra.uiElementsHidden = false;
}

(function () {
    initializePlayerState();
    initializeVideoWatchTimeState();

    messageEventListenerMap[MAIN_LISTENER_ID] = function (
        message: Message<PageMetadata>,
    ) {
        if (
            ![FRAME_INIT_MESSAGE_TYPE, METADATA_CHANGE_MESSAGE_TYPE].includes(
                message.type,
            )
        ) {
            return;
        }

        if (pageMetadata?.isDesktop !== message.data.isDesktop) {
            if (message.data.isDesktop) {
                document.documentElement.setAttribute('data-is-desktop', '1');
            } else {
                document.documentElement.removeAttribute('data-is-desktop');
            }
        }

        pageMetadata = message.data;

        // NOTE: The INIT message should fire only once
        if (message.type === FRAME_INIT_MESSAGE_TYPE) {
            const pageUrl = pageMetadata.url;

            const urlData = new URL(pageUrl);

            const source = urlData.origin + urlData.pathname;

            initializeEventTracker({
                apiUrl: `https://${window.location.hostname}/api/`,
                workspaceId: workspace.code,
                flowId: `${flowConfig.id}`,
                isPreviewMode: pageMetadata.isPreviewMode,
                source: source,
                visitorId: pageMetadata.visitorId,
            });

            // NOTE: Render the first video only when metadata received
            renderVideo();
        }
    };

    setupMessageListener();

    setupKeyDownListener();

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            getEventTracker().submit();
        }
    });
})();
